<template>
  <v-dialog
    v-model="confirmDialog"
    :close-on-content-click="false"
    :nudge-right="40"
    transition="scale-transition"
    offset-y
    width="100%"
    max-width="600px"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-card-text style="padding: 0px">
        <v-btn
          color="indigo"
          outlined
          v-bind="attrs"
          v-on="on"
          block
          large
          :disabled="trip.transferred_at || isDepartureTimePassed"
        >
          <v-icon class="mr-2">mdi-transfer</v-icon>
          {{
            trip.transferred_at
              ? "Trip Transferred"
              : isDepartureTimePassed
              ? "Departure Time Passed"
              : "Transfer Trip"
          }}
        </v-btn>
      </v-card-text>
    </template>
    <v-card style="padding: 30px">
      <!-- Provider Input -->
      <v-row>
        <v-col class="noHorizontalPadding">
          <v-text-field
            v-model="provider"
            label="Provider"
            outlined
            prepend-inner-icon="mdi-office-building"
            :rules="[(v) => !!v || 'Provider is required']"
            placeholder="Enter provider name"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Boat Input -->
      <v-row style="margin-top: -30px">
        <v-col class="noHorizontalPadding">
          <v-text-field
            v-model="boat"
            label="Boat"
            outlined
            prepend-inner-icon="mdi-ferry"
            :rules="[(v) => !!v || 'Boat is required']"
            placeholder="Enter boat name"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- New Time -->
      <v-row>
        <v-col class="noHorizontalPadding">
          <v-dialog
            v-model="dateModal"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                outlined
                :value="computedDate"
                label="New Date"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="newDate"
              @input="dateModal = false"
              dark
            ></v-date-picker>
          </v-dialog>
        </v-col>
      </v-row>

      <v-row style="margin-top: -30px">
        <v-col cols="6" class="noHorizontalPadding">
          <v-dialog v-model="timeModal" width="290px">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="newTime"
                label="Departure Time"
                prepend-inner-icon="mdi-clock-time-four-outline"
                v-bind="attrs"
                v-on="on"
                readonly
                outlined
              ></v-text-field>
            </template>
            <v-time-picker
              v-model="newTime"
              @input="timeModal = false"
              dark
            ></v-time-picker>
          </v-dialog>
        </v-col>
        <v-col cols="6" class="noHorizontalPadding">
          <v-dialog v-model="arrivalTimeModal" width="290px">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="newArrivalTime"
                label="Arrival Time"
                prepend-inner-icon="mdi-clock-time-four-outline"
                v-bind="attrs"
                v-on="on"
                readonly
                outlined
              ></v-text-field>
            </template>
            <v-time-picker
              v-model="newArrivalTime"
              @input="arrivalTimeModal = false"
              dark
            ></v-time-picker>
          </v-dialog>
        </v-col>
      </v-row>

      <!-- Contact Number -->
      <v-row style="margin-top: -30px">
        <v-col class="noHorizontalPadding">
          <v-text-field
            v-model="contactNumber"
            label="Contact Number"
            outlined
            prepend-inner-icon="mdi-phone"
            placeholder="7 digits (local number)"
            :rules="contactNumberRules"
            maxlength="7"
            @input="validateContactNumber"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Remarks -->
      <v-row style="margin-top: -30px">
        <v-col class="noHorizontalPadding">
          <v-textarea
            v-model="remarks"
            label="Additional Remarks"
            outlined
            prepend-inner-icon="mdi-note-text"
            rows="3"
            placeholder="Enter any additional information for customers..."
          ></v-textarea>
        </v-col>
      </v-row>

      <v-row style="margin-top: -10px; justify-content: space-between">
        <v-btn text @click="confirmDialog = false">Cancel</v-btn>
        <v-btn
          color="indigo"
          outlined
          :loading="transferring"
          @click="transferTrip()"
        >
          <v-icon class="mr-2">mdi-transfer</v-icon>
          Transfer Trip
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import axios from "axios";

export default {
  name: "TransferTrip",
  props: {
    trip: Object,
    getTrips: Function,
    message: Function,
    errorMessage: Function,
  },
  data: () => ({
    confirmDialog: null,
    transferring: false,
    provider: "",
    boat: "",
    newDate: null,
    newTime: null,
    newArrivalTime: null,
    dateModal: null,
    timeModal: null,
    arrivalTimeModal: null,
    contactNumber: "",
    remarks: "",
    contactNumberRules: [
      (v) => !v || v.length === 7 || "Contact number must be exactly 7 digits",
      (v) => !v || /^\d+$/.test(v) || "Contact number must contain only digits",
    ],
  }),
  computed: {
    computedDate() {
      return this.newDate
        ? moment(this.newDate).format("dddd, D MMMM YYYY")
        : "";
    },
    isDepartureTimePassed() {
      if (!this.trip || !this.trip.fromTime) {
        return false;
      }
      return moment().isAfter(moment(this.trip.fromTime));
    },
  },
  methods: {
    validateContactNumber() {
      // Remove any non-digit characters
      this.contactNumber = this.contactNumber.replace(/\D/g, "");
    },

    transferTrip() {
      if (!this.provider || !this.provider.trim()) {
        this.errorMessage("Please enter a provider.");
        return;
      }
      if (!this.boat || !this.boat.trim()) {
        this.errorMessage("Please enter a boat.");
        return;
      }
      if (!this.newDate || !this.newTime) {
        this.errorMessage("Please select new date and time.");
        return;
      }
      if (this.contactNumber && this.contactNumber.length !== 7) {
        this.errorMessage("Contact number must be exactly 7 digits.");
        return;
      }

      const newDateTime = moment(
        `${this.newDate} ${this.newTime}`,
        "YYYY-MM-DD HH:mm"
      );

      let newArrivalDateTime = null;
      if (this.newArrivalTime) {
        newArrivalDateTime = moment(
          `${this.newDate} ${this.newArrivalTime}`,
          "YYYY-MM-DD HH:mm"
        );

        if (newArrivalDateTime.isSameOrBefore(newDateTime)) {
          this.errorMessage("Arrival time must be after departure time.");
          return;
        }
      }

      const data = {
        tripId: this.trip.id,
        provider: this.provider.trim(),
        boat: this.boat.trim(),
        newTime: newDateTime.toISOString(),
        newArrivalTime: newArrivalDateTime
          ? newArrivalDateTime.toISOString()
          : null,
        contactNumber: this.contactNumber,
        remarks: this.remarks,
      };

      this.transferring = true;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/transfertrip`,
        method: "POST",
        headers: { Authorization: localStorage.getItem("Authorization") },
        data: JSON.stringify(data),
      })
        .then((resp) => {
          this.message(
            resp.data.message +
              ` (${resp.data.transferredBookings} bookings transferred)`
          );
          this.getTrips();
          this.confirmDialog = false;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => (this.transferring = false));
    },
  },
  watch: {
    confirmDialog: {
      handler: function () {
        if (this.confirmDialog) {
          // Pre-fill with original trip time
          this.newDate = moment(this.trip.fromTime)
            .startOf("day")
            .format("YYYY-MM-DD");
          this.newTime = moment(this.trip.fromTime).format("HH:mm");
          // Auto-calculate arrival time (30 minutes after departure)
          this.newArrivalTime = moment(this.trip.fromTime)
            .add(30, "minutes")
            .format("HH:mm");
          this.provider = "";
          this.boat = "";
          this.contactNumber = "";
          this.remarks = "";
        }
      },
    },
    newTime: {
      handler: function () {
        // Auto-update arrival time when departure time changes
        if (this.newTime && this.newDate) {
          this.newArrivalTime = moment(
            `${this.newDate} ${this.newTime}`,
            "YYYY-MM-DD HH:mm"
          )
            .add(30, "minutes")
            .format("HH:mm");
        }
      },
    },
  },
};
</script>

<style scoped>
.noHorizontalPadding {
  padding-left: 0px;
  padding-right: 0px;
}
.noVerticalPadding {
  padding-top: 0px;
  padding-bottom: 0px;
}
</style>
